/**
 * Portal access, granted MATTER BY MATTER (CR-21, Master Spec 6.5).
 *
 * ===========================================================================
 * WHY THIS SCREEN IS SHAPED THE WAY IT IS
 * ===========================================================================
 * Access is per contact and per matter, not per client. Joint clients and corporate
 * clients with several authorised contacts are normal, and a contact added for one
 * matter has no business reading another — the second spouse in a probate file, the
 * director brought in for one transaction, the interpreter. A client-level switch would
 * be the whole leak in a single control, so there is no client-level switch here.
 *
 * Two separate acts, deliberately separated in the interface as they are in the API:
 *
 *   INVITING opens the door. For anybody but the primary contact it grants sight of
 *   nothing at all: it sets `portal_enabled` on the client and sends the contact a
 *   credential. The PRIMARY contact is the one automatic grant -- inviting them shows
 *   them the client's live matters, and every matter opened afterwards, because a client
 *   signed in to an empty portal while the firm worked on their file was a real failure. It requires an `Idempotency-Key`, because a retry after a timeout must not
 *   invite the same person twice.
 *
 *   GRANTING decides what they can actually read, one matter at a time. The table this
 *   writes to IS the portal's scope resolver: it is read on every portal request and
 *   never cached, so a revocation here takes effect on the client's very next call
 *   rather than at the end of a cache window.
 *
 * Granting is additionally gated server-side on the acting staff member being able to
 * see the matter themselves, which is why this table lists only the matters this reader
 * can see. You cannot show a client something you cannot see.
 *
 * Every change is confirmed with the consequence stated in full, because a mis-click
 * here hands somebody outside the firm sight of a file — and unlike most mistakes in
 * this product, that one cannot be taken back by undoing it.
 * ===========================================================================
 */

import {
  ConflictError,
  STAFF,
  newIdempotencyKey,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  usePaginatedList,
  type CaseListItem,
  type ClientContactRecord,
  type ClientDetail,
  type ContactMatterAccessRecord,
  type MatterAccessLevel,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Select,
  Skeleton,
  useToast,
} from '@legalcrm/ui';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { Can } from '@/auth/permissions';
import { formatDateTime } from '@/lib/format';

import { ACCESS_LEVEL_OPTIONS, accessLevelLabel } from './contactOptions';

interface PendingChange {
  contact: ClientContactRecord;
  matter: CaseListItem;
  level: MatterAccessLevel;
  current: MatterAccessLevel;
}

export function ClientPortalAccessTab({ client }: { client: ClientDetail }) {
  const { matterLabel } = useAuth();
  const [contactId, setContactId] = useState(
    () => client.contacts.find((contact) => contact.is_primary)?.id ?? client.contacts[0]?.id ?? '',
  );

  const contact = client.contacts.find((row) => row.id === contactId) ?? null;

  const matters = usePaginatedList<CaseListItem>(
    qk.sub('clients', client.id, 'matters'),
    STAFF.cases,
    { client: client.id, limit: 100 },
  );

  if (client.contacts.length === 0) {
    return (
      <Card>
        <CardHeader title="Portal access" />
        <CardBody>
          <EmptyState
            title="There is nobody to give access to"
            description="Portal access belongs to a named contact. Add a contact first."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <InvitationCard client={client} />

      <Card>
        <CardHeader
          title={`What ${contact?.full_name ?? 'this contact'} can see`}
          description={`One row per ${matterLabel.toLowerCase()}. Access is decided per ${matterLabel.toLowerCase()} and per contact — never for the client as a whole.`}
          actions={
            <div className="w-64">
              <Select
                label="Contact"
                hideLabel
                options={client.contacts.map((row) => ({
                  value: row.id,
                  label: row.is_primary ? `${row.full_name} (primary)` : row.full_name,
                }))}
                value={contactId}
                onChange={(event) => setContactId(event.target.value)}
              />
            </div>
          }
        />
        <CardBody>
          {contact ? (
            <MatterAccessTable
              client={client}
              contact={contact}
              matters={matters.items}
              mattersLoading={matters.isLoading}
              mattersError={matters.error}
              onRetryMatters={() => void matters.refetch()}
              hasMoreMatters={matters.hasNext}
            />
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * The invitation, kept visually apart from the grants because it is a different act
 * with a different effect. Everything on this card opens a door; nothing on it shows
 * anybody anything.
 */
function InvitationCard({ client }: { client: ClientDetail }) {
  const api = useApi();
  const { toast } = useToast();
  const [inviting, setInviting] = useState<ClientContactRecord | null>(null);

  // ONE key per user-initiated attempt, held across retries of that attempt. A fresh
  // key per retry defeats the mechanism entirely, and the symptom is a client invited
  // into the portal twice.
  const idempotencyKey = useRef<string | null>(null);

  const invite = useApiMutation<ClientDetail, { contactId: string }>(
    {
      mutationFn: ({ contactId }) => {
        idempotencyKey.current ??= newIdempotencyKey();
        return api.post<ClientDetail>(
          `${STAFF.clientInvitePortal(client.id)}/`,
          { contact: contactId },
          { idempotencyKey: idempotencyKey.current },
        );
      },
      // `contacts` and `cases` too: inviting the primary contact grants matters, which
      // changes the table below and every matter's "the client cannot see this" notice.
      invalidates: [qk.all('clients'), qk.all('contacts'), qk.all('cases')],
    },
    {
      onSuccess: () => {
        idempotencyKey.current = null;
        toast({
          title: 'Invitation recorded',
          description: inviting?.is_primary
            ? 'The portal is open to them. As the primary contact they can see the client’s live matters, and any matter opened from now on.'
            : 'The portal is open to them. They can see nothing until a matter is granted below.',
          tone: 'success',
        });
        setInviting(null);
      },
      onError: (error) => {
        // The key is kept: this attempt may be retried, and a retry must carry the
        // same key. A 409 here is a business-rule refusal — no email address, a
        // removed contact — and its wording is written for the reader.
        toast({
          title: 'The invitation was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The invitation could not be sent. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <>
      <Card>
        <CardHeader
          title="Portal invitation"
          description="An invitation opens the portal to one named contact. The primary contact is shown the client’s live matters; anybody else sees nothing until a matter is granted below."
        />
        <CardBody>
          <ul className="flex flex-col divide-y divide-line">
            {client.contacts.map((contact) => (
              <li
                key={contact.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-base text-ink">{contact.full_name}</p>
                  <p className="text-sm text-ink-muted">
                    {contact.email || 'No email address — there is nowhere to send an invitation.'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/*
                    Plain English, not the domain's word for it. "Holds no grants" is
                    what the data model calls this, and it read to the first person who
                    saw it as though something had failed. What it actually means is
                    that this person can open the portal and find nothing in it, which
                    is worth saying in those words -- it is the difference between an
                    invitation that helps and one that produces a phone call.
                  */}
                  <Badge tone={contact.has_portal_access ? 'info' : 'neutral'} size="sm">
                    {contact.has_portal_access
                      ? 'Can see at least one matter'
                      : 'Cannot see any matter yet'}
                  </Badge>
                  <Can actions={client._actions} action="edit">
                    {/*
                      Secondary, not ghost. A ghost button has no border until it is
                      hovered, so this read as a line of text -- and it is the only way
                      to give a client access to anything on this screen.
                    */}
                    <Button variant="secondary" size="sm" onClick={() => setInviting(contact)}>
                      Invite to portal
                    </Button>
                  </Can>
                </div>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <ConfirmModal
        open={inviting !== null}
        onClose={() => setInviting(null)}
        title="Invite this contact to the portal"
        description={
          inviting
            ? `${inviting.full_name} will be sent a credential for ${client.display_name}’s portal.`
            : ''
        }
        consequences={
          inviting?.is_primary
            ? 'As the primary contact they will also see this client’s live matters, and every matter opened for the client from now on. A matter already withdrawn from them stays withdrawn.'
            : 'This opens the portal to them and nothing more. They will see no matter, no document and no message until a matter is granted to them individually.'
        }
        confirmLabel="Send invitation"
        loading={invite.isPending}
        onConfirm={() => inviting && invite.mutate({ contactId: inviting.id })}
      />
    </>
  );
}

function MatterAccessTable({
  client,
  contact,
  matters,
  mattersLoading,
  mattersError,
  onRetryMatters,
  hasMoreMatters,
}: {
  client: ClientDetail;
  contact: ClientContactRecord;
  matters: CaseListItem[];
  mattersLoading: boolean;
  mattersError: unknown;
  onRetryMatters: () => void;
  hasMoreMatters: boolean;
}) {
  const api = useApi();
  const { toast } = useToast();
  const { matterLabel } = useAuth();
  const [pending, setPending] = useState<PendingChange | null>(null);

  const grants = useApiQuery<{ results: ContactMatterAccessRecord[] }>(
    qk.sub('contacts', contact.id, 'matter-access'),
    STAFF.contactMatterAccess(contact.id),
  );

  const setAccess = useApiMutation<unknown, PendingChange>(
    {
      // One write covers grant, change and revoke: `none` is expressed as a
      // revocation server-side, so there is a single representation of "no access"
      // rather than two that have to agree with each other.
      mutationFn: (change) =>
        api.post(`${STAFF.contactMatterAccess(contact.id)}/`, {
          case: change.matter.id,
          access_level: change.level,
        }),
      // `cases` too, so a matter's "the client cannot see this" notice follows the grant.
      invalidates: [
        qk.sub('contacts', contact.id, 'matter-access'),
        qk.all('clients'),
        qk.all('cases'),
      ],
    },
    {
      onSuccess: (_result, change) => {
        toast({
          title: change.level === 'none' ? 'Access revoked' : 'Access granted',
          description:
            change.level === 'none'
              ? `${contact.full_name} loses sight of ${change.matter.case_number} on their next request.`
              : `${contact.full_name} can now see ${change.matter.case_number} — ${accessLevelLabel(change.level).toLowerCase()}.`,
          tone: 'success',
        });
        setPending(null);
      },
      onError: (error) => {
        toast({
          title: 'The change was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The access level could not be changed. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  if (grants.isLoading || mattersLoading) {
    return <Skeleton className="h-40 w-full" />;
  }
  if (grants.error) {
    return <ErrorState error={grants.error} onRetry={() => void grants.refetch()} />;
  }
  if (mattersError) {
    return <ErrorState error={mattersError} onRetry={onRetryMatters} />;
  }

  const rows = grants.data?.results ?? [];
  /** A grant is LIVE only while `revoked_at` is null. Revoked rows stay as history. */
  const liveFor = (caseId: string): ContactMatterAccessRecord | undefined =>
    rows.find((row) => row.case_id === caseId && row.revoked_at === null);

  if (matters.length === 0) {
    return (
      <EmptyState
        title={`No ${matterLabel.toLowerCase()}s to show`}
        description={`Access is granted against a ${matterLabel.toLowerCase()}, and there are none here for you to grant.`}
      />
    );
  }

  return (
    <>
      <ul className="flex flex-col divide-y divide-line">
        {matters.map((matter) => {
          const live = liveFor(matter.id);
          const current: MatterAccessLevel = live?.access_level ?? 'none';
          return (
            <li
              key={matter.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <Link
                  to={`/cases/${matter.id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {matter.case_number}
                </Link>
                <p className="truncate text-sm text-ink-muted">{matter.title}</p>
                {live && (
                  <p className="mt-0.5 text-xs text-ink-subtle">
                    Granted {formatDateTime(live.granted_at)}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-3">
                {/* The level in words, not only in the select — this is the line a
                    reader scans to answer "can this person see that file". */}
                <Badge tone={current === 'none' ? 'neutral' : 'info'} size="sm">
                  {accessLevelLabel(current)}
                </Badge>

                <Can actions={client._actions} action="edit">
                  <div className="w-52">
                    <Select
                      label={`Access to ${matter.case_number}`}
                      hideLabel
                      options={ACCESS_LEVEL_OPTIONS.map((option) => ({
                        value: option.value,
                        label: option.label,
                      }))}
                      value={current}
                      onChange={(event) =>
                        setPending({
                          contact,
                          matter,
                          level: event.target.value as MatterAccessLevel,
                          current,
                        })
                      }
                    />
                  </div>
                </Can>
              </div>
            </li>
          );
        })}
      </ul>

      {hasMoreMatters && (
        <p className="mt-3 text-sm text-ink-muted">
          Only the first hundred {matterLabel.toLowerCase()}s are shown.
        </p>
      )}

      <RevokedHistory rows={rows} matters={matters} />

      <ConfirmModal
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending?.level === 'none' ? 'Revoke access to this matter' : 'Grant access to this matter'}
        description={
          pending
            ? `${pending.contact.full_name} · ${pending.matter.case_number} — ${pending.matter.title}`
            : ''
        }
        consequences={
          pending?.level === 'none'
            ? 'They lose sight of this matter on their very next request in the portal. The record of what they could see, and when, is kept.'
            : ACCESS_LEVEL_OPTIONS.find((option) => option.value === pending?.level)?.description
        }
        confirmLabel={pending?.level === 'none' ? 'Revoke access' : 'Grant access'}
        destructive={pending?.level === 'none'}
        loading={setAccess.isPending}
        onConfirm={() => pending && setAccess.mutate(pending)}
      />
    </>
  );
}

/**
 * Revoked grants, kept visible.
 *
 * "What was this person shown, and when" has to stay answerable, and the rows are
 * already in the payload. Hiding them would make the screen look like access had never
 * existed.
 */
function RevokedHistory({
  rows,
  matters,
}: {
  rows: ContactMatterAccessRecord[];
  matters: CaseListItem[];
}) {
  const revoked = rows.filter((row) => row.revoked_at !== null);
  if (revoked.length === 0) return null;

  const numberFor = (caseId: string) =>
    matters.find((matter) => matter.id === caseId)?.case_number ?? 'A matter you cannot see';

  return (
    <div className="mt-4 border-t border-line pt-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
        Access previously held
      </h3>
      <ul className="mt-2 flex flex-col gap-1">
        {revoked.map((row) => (
          <li key={row.id} className="text-sm text-ink-muted">
            {numberFor(row.case_id)} · {accessLevelLabel(row.access_level)} ·{' '}
            {formatDateTime(row.granted_at)} to {formatDateTime(row.revoked_at)}
          </li>
        ))}
      </ul>
    </div>
  );
}
